'use client';

import React from 'react';
import { useRouter } from '@/lib/router';
import {
  adminDashboard, adminTenants, adminUsers, adminSubscriptions,
  adminPromos, adminAffiliateWithdrawals, waNodes, aiProviders,
  toolRegistry, auditLogs, securityDashboard, systemSettings, plans,
  formatRupiah, formatNumber,
} from '@/lib/mock-data';
import {
  StatsCard, StatusBadge, QuotaBar, PageHeader, DataTable,
  SearchInput, TabFilter, EmptyState,
  Modal, FormField, FormInput, FormSelect, FormTextarea, Toggle,
  ConfirmDialog, useToast, Toast, DropdownMenu, DropdownItem,
} from '@/components/shared';

// ============================================
// HELPER: Three-dot menu icon
// ============================================
const DotsIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" /></svg>
);

const ModalFooter = ({ onCancel, onSave }: { onCancel: () => void; onSave: () => void }) => (
  <div className="flex justify-end gap-3">
    <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
    <button onClick={onSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
  </div>
);

type WANode = (typeof waNodes)[number];
type AIProvider = (typeof aiProviders)[number];
type AIModel = AIProvider['models'][number];
type Tool = (typeof toolRegistry)[number];
type AuditLog = (typeof auditLogs)[number];
type Plan = (typeof plans)[number];
type Promo = (typeof adminPromos)[number];
type Subscription = (typeof adminSubscriptions)[number];

// ============================================
// 1. ADMIN DASHBOARD
// ============================================
export function AdminDashboardPage() {
  const navigate = useRouter((s) => s.navigate);
  const d = adminDashboard;
  const maxMrr = Math.max(...d.mrr_chart.map(m => m.mrr));

  const recentActivity = [
    { time: '14:32', text: 'Tenant "Toko Elektronik Jaya" upgrade ke Business', color: 'text-emerald-400' },
    { time: '13:20', text: 'Subscription #2 dibatalkan oleh budi@tokobudi.com', color: 'text-red-400' },
    { time: '12:00', text: 'Pembayaran INV-2025-0042 berhasil — Rp 299.000', color: 'text-emerald-400' },
    { time: '10:00', text: 'Login gagal 15x dari IP 203.0.113.42', color: 'text-red-400' },
    { time: '09:30', text: 'Rate limit triggered dari IP 198.51.100.0', color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Ringkasan Platform Rekanku" />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatsCard title="MRR" value={formatRupiah(d.mrr)} trend={`${d.mrr_growth_percent}%`} trendUp={true} iconBg="bg-emerald-500/20" iconColor="text-emerald-400" />
        <StatsCard title="ARR" value={formatRupiah(d.arr)} iconBg="bg-blue-500/20" iconColor="text-blue-400" />
        <StatsCard title="Tenant" value={String(d.total_tenants)} trend={`+12`} trendUp={true} iconBg="bg-blue-500/20" iconColor="text-blue-400" />
        <StatsCard title="Churn" value={`${d.churn_rate_percent}%`} iconBg="bg-red-500/20" iconColor="text-red-400" />
        <StatsCard title="Conversations/hr" value={formatNumber(d.conversations_per_hour)} iconBg="bg-purple-500/20" iconColor="text-purple-400" />
        <StatsCard title="User Baru" value={`+${d.new_users_today}`} trend={null} iconBg="bg-orange-500/20" iconColor="text-orange-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-200">MRR Growth (6 bulan)</h3>
            <span className="text-xs text-emerald-400 font-medium">▲ {d.mrr_growth_percent}% MoM</span>
          </div>
          <div className="flex items-end gap-2 h-40">
            {d.mrr_chart.map((m) => {
              const heightPct = (m.mrr / maxMrr) * 100;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                  <span className="text-xs text-slate-400">{formatRupiah(m.mrr).replace('Rp', '')}</span>
                  <div className="w-full bg-slate-700/50 rounded-t" style={{ height: '120px' }}>
                    <div className="w-full bg-gradient-to-t from-orange-600 to-orange-400 rounded-t transition-all duration-700" style={{ height: `${heightPct}%` }} />
                  </div>
                  <span className="text-xs text-slate-500">{m.month}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-200 mb-3">WA Nodes</h3>
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-slate-400">Online</span>
                  <span className="text-xs text-slate-300">{d.wa_nodes_online}/5</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${(d.wa_nodes_online / 5) * 100}%` }} />
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-400">
              <span className="text-slate-200 font-medium">{d.active_wa_sessions}</span> sesi aktif
            </div>
          </div>

          <div className="bg-slate-800 border border-red-500/30 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-200 mb-3">Security</h3>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs text-red-400 font-medium">3 IP mencurigakan terdeteksi</span>
            </div>
            <div className="text-xs text-slate-400">
              Login gagal: <span className="text-slate-200">{securityDashboard.failed_logins_24h}x</span> dalam 24 jam
            </div>
            <button onClick={() => navigate('/admin/security')} className="mt-3 w-full text-center text-xs text-orange-400 hover:text-orange-300 py-1.5 bg-orange-500/10 rounded-lg transition-colors">
              Lihat Detail →
            </button>
          </div>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-200 mb-4">Aktivitas Terbaru</h3>
        <div className="space-y-3">
          {recentActivity.map((a, i) => (
            <div key={i} className="flex items-start gap-3 text-xs">
              <span className="text-slate-500 shrink-0 w-10 text-right tabular-nums">{a.time}</span>
              <div className="w-1.5 h-1.5 rounded-full bg-slate-600 mt-1.5 shrink-0" />
              <span className="text-slate-300">{a.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 2. ADMIN TENANTS
// ============================================
export function AdminTenantsPage() {
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('semua');
  const [planFilter, setPlanFilter] = React.useState('semua');
  const [selected, setSelected] = React.useState<Set<number>>(new Set());

  const filtered = adminTenants.filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.owner_email.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'semua' && t.status !== statusFilter) return false;
    if (planFilter !== 'semua' && t.plan.toLowerCase() !== planFilter) return false;
    return true;
  });

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(t => t.id)));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tenant (234)"
        actions={
          <button className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">
            + Buat Tenant
          </button>
        }
      />

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 max-w-sm">
          <SearchInput placeholder="Cari tenant..." value={search} onChange={setSearch} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
            <option value="semua">Semua Status</option>
            <option value="active">Aktif</option>
            <option value="trial">Trial</option>
            <option value="suspended">Suspend</option>
            <option value="expired">Expired</option>
          </select>
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
            <option value="semua">Semua Plan</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="business">Business</option>
          </select>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2.5 w-8">
                  <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} className="rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500/50" />
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Nama</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Owner Email</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Plan</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase w-10">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <React.Fragment key={t.id}>
                  <tr className={`border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${selected.has(t.id) ? 'bg-orange-500/5' : ''}`}>
                    <td className="px-3 py-2.5 h-10">
                      <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} className="rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500/50" />
                    </td>
                    <td className="px-3 py-2.5"><span className="font-semibold text-slate-100">{t.name}</span></td>
                    <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell">{t.owner_email}</td>
                    <td className="px-3 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.plan === 'business' ? 'bg-purple-500/20 text-purple-400' : t.plan === 'pro' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-600/50 text-slate-400'}`}>{t.plan}</span>
                    </td>
                    <td className="px-3 py-2.5"><StatusBadge status={t.status} /></td>
                    <td className="px-3 py-2.5 text-right">
                      <button className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-600/50 transition-colors"><DotsIcon /></button>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-700/30 hover:bg-slate-700/20">
                    <td className="px-3 py-1.5" />
                    <td colSpan={5} className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-slate-500">
                        <span>{t.business_type}</span>
                        <span>👥 {t.members}</span>
                        <span>🤖 {t.agents} agent</span>
                        <span>📱 {t.wa_sessions} WA</span>
                        <span>Bergabung {t.created}</span>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-700 border border-slate-600 rounded-xl px-5 py-3 flex items-center gap-4 shadow-2xl">
          <span className="text-xs text-slate-200 font-medium">✓ {selected.size} item dipilih</span>
          <div className="w-px h-5 bg-slate-600" />
          <button className="text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors">Suspend</button>
          <button className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors">Hapus</button>
          <button className="text-xs text-slate-400 hover:text-white font-medium transition-colors" onClick={() => setSelected(new Set())}>Batal</button>
        </div>
      )}
    </div>
  );
}

// ============================================
// 3. ADMIN USERS
// ============================================
export function AdminUsersPage() {
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState('semua');
  const [statusFilter, setStatusFilter] = React.useState('semua');
  const [userStates, setUserStates] = React.useState<Record<number, string>>(
    Object.fromEntries(adminUsers.map(u => [u.id, u.status]))
  );

  const filtered = adminUsers.filter(u => {
    if (search && !u.name.toLowerCase().includes(search.toLowerCase()) && !u.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (roleFilter !== 'semua' && !u.roles.includes(roleFilter)) return false;
    if (statusFilter !== 'semua' && userStates[u.id] !== statusFilter) return false;
    return true;
  });

  const toggleUserStatus = (id: number) => {
    setUserStates(prev => ({ ...prev, [id]: prev[id] === 'active' ? 'inactive' : 'active' }));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pengguna (1,847)"
        actions={
          <div className="flex gap-2">
            <button className="bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Export CSV
            </button>
            <button className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">
              + Tambah User
            </button>
          </div>
        }
      />

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 max-w-sm">
          <SearchInput placeholder="Cari user..." value={search} onChange={setSearch} />
        </div>
        <div className="flex gap-2">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
            <option value="semua">Semua Role</option>
            <option value="superadmin">Superadmin</option>
            <option value="tenant_member">Tenant Member</option>
            <option value="affiliate">Affiliate</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
            <option value="semua">Semua Status</option>
            <option value="active">Aktif</option>
            <option value="inactive">Nonaktif</option>
          </select>
        </div>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Name</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Email</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Roles</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Last Login</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Tenants</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase w-16">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                  <td className="px-3 py-2.5 h-10">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 text-[10px] font-bold shrink-0">
                        {u.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <span className="font-medium text-slate-100 truncate max-w-[120px]">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell">{u.email}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1 flex-wrap">
                      {u.roles.map(r => (
                        <span key={r} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r === 'superadmin' ? 'bg-red-500/20 text-red-400' : r === 'affiliate' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'}`}>{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><button onClick={() => toggleUserStatus(u.id)}><StatusBadge status={userStates[u.id]} /></button></td>
                  <td className="px-3 py-2.5 text-slate-400 hidden lg:table-cell tabular-nums">{u.last_login}</td>
                  <td className="px-3 py-2.5 hidden lg:table-cell">
                    {u.tenants.length > 0 ? u.tenants.map(t => <span key={t} className="text-slate-400">{t}</span>) : <span className="text-slate-500">-</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-600/50 transition-colors"><DotsIcon /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 4. ADMIN SUBSCRIPTIONS (with View/Cancel/Reactivate modals)
// ============================================
export function AdminSubscriptionsPage() {
  const { toast, show: showToast } = useToast();
  const [activeTab, setActiveTab] = React.useState('semua');
  const [viewSub, setViewSub] = React.useState<Subscription | null>(null);
  const [cancelSub, setCancelSub] = React.useState<Subscription | null>(null);
  const [reactivateSub, setReactivateSub] = React.useState<Subscription | null>(null);
  const [cancelReason, setCancelReason] = React.useState('');

  const tabs = [
    { label: 'Semua', value: 'semua', count: adminSubscriptions.length },
    { label: 'Aktif', value: 'active', count: adminSubscriptions.filter(s => s.status === 'active').length },
    { label: 'Trial', value: 'trial', count: adminSubscriptions.filter(s => s.status === 'trial').length },
    { label: 'Suspended', value: 'suspended', count: adminSubscriptions.filter(s => s.status === 'suspended').length },
    { label: 'Cancelled', value: 'cancelled', count: adminSubscriptions.filter(s => s.status === 'cancelled').length },
  ];

  const filtered = activeTab === 'semua' ? adminSubscriptions : adminSubscriptions.filter(s => s.status === activeTab);
  const totalMRR = adminSubscriptions.filter(s => s.status === 'active').reduce((acc, s) => acc + s.amount, 0);

  const invoiceHistory = [
    { id: 'INV-2025-0042', period: 'Jan 2025', amount: 299000, status: 'paid' },
    { id: 'INV-2025-0041', period: 'Des 2024', amount: 299000, status: 'paid' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Langganan" />
      <Toast toast={toast} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard title="Total Aktif" value={String(adminSubscriptions.filter(s => s.status === 'active').length)} iconBg="bg-emerald-500/20" iconColor="text-emerald-400" />
        <StatsCard title="Trial" value={String(adminSubscriptions.filter(s => s.status === 'trial').length)} iconBg="bg-blue-500/20" iconColor="text-blue-400" />
        <StatsCard title="Suspended" value={String(adminSubscriptions.filter(s => s.status === 'suspended').length)} iconBg="bg-red-500/20" iconColor="text-red-400" />
        <StatsCard title="MRR Aktif" value={formatRupiah(totalMRR)} iconBg="bg-emerald-500/20" iconColor="text-emerald-400" />
      </div>

      <TabFilter tabs={tabs} active={activeTab} onChange={setActiveTab} />

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">ID</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Tenant</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Plan</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Mulai</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Berakhir</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase">Amount</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase w-10">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                  <td className="px-3 py-2.5 h-10 text-slate-400 tabular-nums">#{s.id}</td>
                  <td className="px-3 py-2.5 font-medium text-slate-100">{s.tenant}</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${s.plan === 'business' ? 'bg-purple-500/20 text-purple-400' : s.plan === 'pro' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-600/50 text-slate-400'}`}>{s.plan}</span>
                  </td>
                  <td className="px-3 py-2.5"><StatusBadge status={s.status} /></td>
                  <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell tabular-nums">{s.start}</td>
                  <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell tabular-nums">{s.end}</td>
                  <td className="px-3 py-2.5 text-right text-slate-200 font-medium">{formatRupiah(s.amount)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <DropdownMenu trigger={<button className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-600/50 transition-colors"><DotsIcon /></button>}>
                      <DropdownItem onClick={() => setViewSub(s)}>View</DropdownItem>
                      {(s.status === 'active' || s.status === 'trial') && (
                        <DropdownItem danger onClick={() => setCancelSub(s)}>Cancel</DropdownItem>
                      )}
                      {(s.status === 'suspended' || s.status === 'cancelled') && (
                        <DropdownItem onClick={() => setReactivateSub(s)}>Reactivate</DropdownItem>
                      )}
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Subscription Modal */}
      <Modal open={!!viewSub} onClose={() => setViewSub(null)} title="Detail Langganan" size="lg">
        {viewSub && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-700/30 rounded-lg p-3">
                <span className="text-xs text-slate-400">Tenant</span>
                <p className="text-sm font-medium text-slate-100 mt-1">{viewSub.tenant}</p>
              </div>
              <div className="bg-slate-700/30 rounded-lg p-3">
                <span className="text-xs text-slate-400">Plan</span>
                <p className="text-sm font-medium text-slate-100 mt-1 capitalize">{viewSub.plan}</p>
              </div>
              <div className="bg-slate-700/30 rounded-lg p-3">
                <span className="text-xs text-slate-400">Periode</span>
                <p className="text-sm text-slate-200 mt-1">{viewSub.start} — {viewSub.end}</p>
              </div>
              <div className="bg-slate-700/30 rounded-lg p-3">
                <span className="text-xs text-slate-400">Amount / bulan</span>
                <p className="text-sm font-medium text-emerald-400 mt-1">{formatRupiah(viewSub.amount)}</p>
              </div>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-3">
              <span className="text-xs text-slate-400">Status</span>
              <div className="mt-1"><StatusBadge status={viewSub.status} /></div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-300 mb-2">Invoice History</h4>
              <div className="space-y-2">
                {invoiceHistory.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2 text-xs">
                    <div>
                      <span className="text-slate-200 font-mono">{inv.id}</span>
                      <span className="text-slate-500 ml-2">{inv.period}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-200">{formatRupiah(inv.amount)}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel Subscription */}
      <Modal open={!!cancelSub} onClose={() => { setCancelSub(null); setCancelReason(''); }} title="Batalkan Langganan" size="sm" footer={
        <div className="flex justify-end gap-3">
          <button onClick={() => { setCancelSub(null); setCancelReason(''); }} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={() => { setCancelSub(null); setCancelReason(''); showToast('Langganan berhasil dibatalkan'); }} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">Batalkan</button>
        </div>
      }>
        <div className="space-y-3">
          <p className="text-sm text-slate-400">Apakah kamu yakin ingin membatalkan langganan <span className="text-slate-200 font-medium">{cancelSub?.tenant}</span>?</p>
          <FormField label="Alasan pembatalan" required>
            <FormTextarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Masukkan alasan..." rows={3} />
          </FormField>
        </div>
      </Modal>

      {/* Reactivate Subscription */}
      <ConfirmDialog
        open={!!reactivateSub}
        onClose={() => setReactivateSub(null)}
        onConfirm={() => { setReactivateSub(null); showToast('Langganan berhasil diaktifkan kembali'); }}
        title="Aktifkan Kembali"
        message={`Apakah kamu yakin ingin mengaktifkan kembali langganan ${reactivateSub?.tenant}?`}
        confirmText="Aktifkan"
        variant="primary"
      />
    </div>
  );
}

// ============================================
// 5. ADMIN PLANS (with Create/Edit/View modals)
// ============================================
export function AdminPlansPage() {
  const { toast, show: showToast } = useToast();
  const [planStates, setPlanStates] = React.useState<Record<number, boolean>>(Object.fromEntries(plans.map(p => [p.id, p.is_active])));
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editPlan, setEditPlan] = React.useState<Plan | null>(null);
  const [viewPlan, setViewPlan] = React.useState<Plan | null>(null);

  const emptyForm = { name: '', slug: '', price_monthly: 0, price_yearly: 0, trial_days: 14, is_popular: false, is_active: true, max_agents: 1, max_wa_sessions: 1, max_token_per_month: 10000, kb_storage_mb: 100, features: [] as string[], feature_flags: { ai_agent: true, whatsapp: true, analytics: false, white_label: false, integrations: false } };
  const [form, setForm] = React.useState(emptyForm);

  const openCreate = () => { setForm({ ...emptyForm }); setCreateOpen(true); };
  const openEdit = (p: Plan) => {
    setForm({ name: p.name, slug: p.slug, price_monthly: p.price_monthly, price_yearly: p.price_yearly, trial_days: p.trial_days, is_popular: p.is_popular, is_active: p.is_active, max_agents: p.limits.max_agents, max_wa_sessions: p.limits.max_wa_sessions, max_token_per_month: p.limits.max_token_per_month, kb_storage_mb: p.limits.kb_storage_mb, features: p.features, feature_flags: { ...p.feature_flags } });
    setEditPlan(p);
  };

  const togglePlan = (id: number) => { setPlanStates(prev => ({ ...prev, [id]: !prev[id] })); };
  const flagKeys = ['ai_agent', 'whatsapp', 'analytics', 'white_label', 'integrations'] as const;

  return (
    <div className="space-y-6">
      <PageHeader title="Paket Plan" actions={<button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">+ Buat Plan</button>} />
      <Toast toast={toast} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isActive = planStates[plan.id];
          return (
            <div key={plan.id} className={`bg-slate-800 border rounded-xl overflow-hidden transition-all ${plan.is_popular ? 'border-orange-500/50 ring-1 ring-orange-500/20' : 'border-slate-700 hover:border-slate-600'}`}>
              {plan.is_popular && <div className="bg-orange-600 text-white text-center py-1 text-xs font-semibold">⭐ POPULER</div>}
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-100">{plan.name}</h3>
                    <span className="text-xs text-slate-500 font-mono">{plan.slug}</span>
                  </div>
                  <button onClick={() => togglePlan(plan.id)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isActive ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-4' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-slate-100">{formatRupiah(plan.price_monthly)}</span>
                  <span className="text-xs text-slate-500">/bulan</span>
                  <div className="text-xs text-slate-500 mt-0.5">Trial {plan.trial_days} hari</div>
                </div>
                <div className="space-y-1.5 mb-4 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">AI Agent</span><span className="text-slate-200">{plan.limits.max_agents === -1 ? 'Unlimited' : plan.limits.max_agents}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">WA Session</span><span className="text-slate-200">{plan.limits.max_wa_sessions}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Token/bln</span><span className="text-slate-200">{formatNumber(plan.limits.max_token_per_month)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">KB Storage</span><span className="text-slate-200">{plan.limits.kb_storage_mb} MB</span></div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 mb-4">
                  {Object.entries(plan.feature_flags).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-1.5 text-xs">
                      <span>{val ? '✅' : '❌'}</span>
                      <span className="text-slate-400 capitalize">{key.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1 mb-5">
                  {plan.features.map((f, fi) => (
                    <div key={fi} className="flex items-start gap-2 text-xs">
                      <svg className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      <span className="text-slate-300">{f}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setViewPlan(plan)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium py-2 rounded-lg transition-colors">View</button>
                  <button onClick={() => openEdit(plan)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium py-2 rounded-lg transition-colors">Edit</button>
                  <button className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-medium py-2 rounded-lg transition-colors">Hapus</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Plan Modal */}
      <Modal open={createOpen || !!editPlan} onClose={() => { setCreateOpen(false); setEditPlan(null); }} title={editPlan ? 'Edit Plan' : 'Buat Plan Baru'} size="lg" footer={<ModalFooter onCancel={() => { setCreateOpen(false); setEditPlan(null); }} onSave={() => { setCreateOpen(false); setEditPlan(null); showToast(editPlan ? 'Plan berhasil diperbarui' : 'Plan berhasil dibuat'); }} />}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Nama Plan" required><FormInput value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Starter" /></FormField>
            <FormField label="Slug" required><FormInput value={form.slug} onChange={(e) => setForm(p => ({ ...p, slug: e.target.value }))} placeholder="starter" /></FormField>
            <FormField label="Harga Bulanan (Rp)"><FormInput type="number" value={String(form.price_monthly)} onChange={(e) => setForm(p => ({ ...p, price_monthly: Number(e.target.value) }))} /></FormField>
            <FormField label="Harga Tahunan (Rp)"><FormInput type="number" value={String(form.price_yearly)} onChange={(e) => setForm(p => ({ ...p, price_yearly: Number(e.target.value) }))} /></FormField>
            <FormField label="Trial Days"><FormInput type="number" value={String(form.trial_days)} onChange={(e) => setForm(p => ({ ...p, trial_days: Number(e.target.value) }))} /></FormField>
          </div>
          <div className="flex gap-4">
            <Toggle checked={form.is_popular} onChange={(v) => setForm(p => ({ ...p, is_popular: v }))} label="Popular" />
            <Toggle checked={form.is_active} onChange={(v) => setForm(p => ({ ...p, is_active: v }))} label="Aktif" />
          </div>
          <div className="border-t border-slate-700 pt-4">
            <h4 className="text-xs font-semibold text-slate-300 mb-3">Limits</h4>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Max Agents"><FormInput type="number" value={String(form.max_agents)} onChange={(e) => setForm(p => ({ ...p, max_agents: Number(e.target.value) }))} /></FormField>
              <FormField label="Max WA Sessions"><FormInput type="number" value={String(form.max_wa_sessions)} onChange={(e) => setForm(p => ({ ...p, max_wa_sessions: Number(e.target.value) }))} /></FormField>
              <FormField label="Max Token/bulan"><FormInput type="number" value={String(form.max_token_per_month)} onChange={(e) => setForm(p => ({ ...p, max_token_per_month: Number(e.target.value) }))} /></FormField>
              <FormField label="KB Storage (MB)"><FormInput type="number" value={String(form.kb_storage_mb)} onChange={(e) => setForm(p => ({ ...p, kb_storage_mb: Number(e.target.value) }))} /></FormField>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-4">
            <h4 className="text-xs font-semibold text-slate-300 mb-3">Feature Flags</h4>
            <div className="grid grid-cols-3 gap-3">
              {flagKeys.map(k => (
                <Toggle key={k} checked={form.feature_flags[k]} onChange={(v) => setForm(p => ({ ...p, feature_flags: { ...p.feature_flags, [k]: v } }))} label={k.replace('_', ' ')} />
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* View Plan Modal */}
      <Modal open={!!viewPlan} onClose={() => setViewPlan(null)} title={viewPlan?.name || ''} size="lg">
        {viewPlan && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Nama</span><p className="text-sm font-medium text-slate-100 mt-1">{viewPlan.name}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Slug</span><p className="text-sm font-mono text-slate-300 mt-1">{viewPlan.slug}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Harga Bulanan</span><p className="text-sm font-medium text-slate-100 mt-1">{formatRupiah(viewPlan.price_monthly)}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Harga Tahunan</span><p className="text-sm font-medium text-slate-100 mt-1">{formatRupiah(viewPlan.price_yearly)}</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-slate-400">AI Agent</span><p className="text-slate-200 mt-1">{viewPlan.limits.max_agents === -1 ? 'Unlimited' : viewPlan.limits.max_agents}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-slate-400">WA Session</span><p className="text-slate-200 mt-1">{viewPlan.limits.max_wa_sessions}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-slate-400">Token/bulan</span><p className="text-slate-200 mt-1">{formatNumber(viewPlan.limits.max_token_per_month)}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-slate-400">KB Storage</span><p className="text-slate-200 mt-1">{viewPlan.limits.kb_storage_mb} MB</p></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(viewPlan.feature_flags).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5 text-xs bg-slate-700/30 rounded-lg px-3 py-2">
                  <span>{v ? '✅' : '❌'}</span>
                  <span className="text-slate-400 capitalize">{k.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-300 mb-2">Features</h4>
              <div className="space-y-1">{viewPlan.features.map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-slate-300"><svg className="w-3.5 h-3.5 text-emerald-400 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>{f}</div>)}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================
// 6. ADMIN PROMOS (with Create/Edit/View modals)
// ============================================
export function AdminPromosPage() {
  const { toast, show: showToast } = useToast();
  const [promoStates, setPromoStates] = React.useState<Record<number, boolean>>(Object.fromEntries(adminPromos.map(p => [p.id, p.is_active])));
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editPromo, setEditPromo] = React.useState<Promo | null>(null);
  const [viewPromo, setViewPromo] = React.useState<Promo | null>(null);

  const emptyForm = { code: '', discount_type: 'percent' as 'percent' | 'fixed', discount_value: 0, max_uses: 100, used_count: 0, valid_from: '', valid_until: '', is_active: true, applicable_plans: ['starter', 'pro', 'business'] as string[] };
  const [form, setForm] = React.useState(emptyForm);

  const openCreate = () => { setForm({ ...emptyForm }); setCreateOpen(true); };
  const openEdit = (p: Promo) => { setForm({ code: p.code, discount_type: p.discount_type, discount_value: p.discount_value, max_uses: p.max_uses, used_count: p.used_count, valid_from: p.valid_from, valid_until: p.valid_until, is_active: p.is_active, applicable_plans: [...p.applicable_plans] }); setEditPromo(p); };
  const togglePlan = (plan: string) => { setForm(f => ({ ...f, applicable_plans: f.applicable_plans.includes(plan) ? f.applicable_plans.filter(p => p !== plan) : [...f.applicable_plans, plan] })); };
  const togglePromo = (id: number) => { setPromoStates(prev => ({ ...prev, [id]: !prev[id] })); };

  return (
    <div className="space-y-6">
      <PageHeader title="Promo Code" actions={<button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">+ Buat Promo</button>} />
      <Toast toast={toast} />

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Code</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Discount</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Redemption</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Valid Period</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Plans</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase w-10">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {adminPromos.map((p) => {
                const isActive = promoStates[p.id];
                const percent = (p.used_count / p.max_uses) * 100;
                const isFull = p.used_count >= p.max_uses;
                return (
                  <tr key={p.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                    <td className="px-3 py-2.5 h-10"><code className="bg-slate-700/50 text-orange-400 px-2 py-0.5 rounded font-mono text-xs font-semibold">{p.code}</code></td>
                    <td className="px-3 py-2.5 text-slate-200 font-medium">{p.discount_type === 'percent' ? `${p.discount_value}%` : formatRupiah(p.discount_value)}</td>
                    <td className="px-3 py-2.5 min-w-[140px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1"><div className="w-full bg-slate-700 rounded-full h-1.5"><div className={`h-1.5 rounded-full transition-all ${isFull ? 'bg-red-500' : 'bg-orange-500'}`} style={{ width: `${percent}%` }} /></div></div>
                        <span className="text-slate-400 tabular-nums shrink-0">{p.used_count}/{p.max_uses}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell tabular-nums">
                      <div className="flex flex-col"><span>{p.valid_from}</span><span className="text-slate-500">s/d {p.valid_until}</span></div>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <div className="flex gap-1 flex-wrap">
                        {p.applicable_plans.map(pl => (<span key={pl} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${pl === 'business' ? 'bg-purple-500/20 text-purple-400' : pl === 'pro' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-600/50 text-slate-400'}`}>{pl}</span>))}
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><button onClick={() => togglePromo(p.id)}><StatusBadge status={isActive ? 'active' : 'inactive'} /></button></td>
                    <td className="px-3 py-2.5 text-right">
                      <DropdownMenu trigger={<button className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-600/50 transition-colors"><DotsIcon /></button>}>
                        <DropdownItem onClick={() => setViewPromo(p)}>View</DropdownItem>
                        <DropdownItem onClick={() => openEdit(p)}>Edit</DropdownItem>
                        <DropdownItem danger onClick={() => showToast('Promo berhasil dihapus')}>Delete</DropdownItem>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Promo Modal */}
      <Modal open={createOpen || !!editPromo} onClose={() => { setCreateOpen(false); setEditPromo(null); }} title={editPromo ? 'Edit Promo' : 'Buat Promo Baru'} footer={<ModalFooter onCancel={() => { setCreateOpen(false); setEditPromo(null); }} onSave={() => { setCreateOpen(false); setEditPromo(null); showToast(editPromo ? 'Promo berhasil diperbarui' : 'Promo berhasil dibuat'); }} />}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Code" required><FormInput value={form.code} onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="LAUNCH2025" /></FormField>
            <FormField label="Discount Type" required>
              <FormSelect value={form.discount_type} onChange={(e) => setForm(f => ({ ...f, discount_type: e.target.value as 'percent' | 'fixed' }))}>
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed (Rp)</option>
              </FormSelect>
            </FormField>
            <FormField label="Discount Value" required><FormInput type="number" value={String(form.discount_value)} onChange={(e) => setForm(f => ({ ...f, discount_value: Number(e.target.value) }))} /></FormField>
            <FormField label="Max Uses" required><FormInput type="number" value={String(form.max_uses)} onChange={(e) => setForm(f => ({ ...f, max_uses: Number(e.target.value) }))} /></FormField>
            <FormField label="Valid From"><FormInput type="date" value={form.valid_from} onChange={(e) => setForm(f => ({ ...f, valid_from: e.target.value }))} /></FormField>
            <FormField label="Valid Until"><FormInput type="date" value={form.valid_until} onChange={(e) => setForm(f => ({ ...f, valid_until: e.target.value }))} /></FormField>
          </div>
          <Toggle checked={form.is_active} onChange={(v) => setForm(f => ({ ...f, is_active: v }))} label="Aktif" />
          <div>
            <span className="text-sm font-medium text-slate-300 block mb-2">Applicable Plans</span>
            <div className="flex gap-4">
              {(['starter', 'pro', 'business'] as const).map(plan => (
                <label key={plan} className="flex items-center gap-2 cursor-pointer text-sm text-slate-400">
                  <input type="checkbox" checked={form.applicable_plans.includes(plan)} onChange={() => togglePlan(plan)} className="rounded border-slate-600 bg-slate-700 text-orange-500 focus:ring-orange-500/50" />
                  <span className="capitalize">{plan}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* View Promo Modal */}
      <Modal open={!!viewPromo} onClose={() => setViewPromo(null)} title={`Promo: ${viewPromo?.code || ''}`}>
        {viewPromo && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Code</span><p className="text-sm font-mono text-orange-400 font-semibold mt-1">{viewPromo.code}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Discount</span><p className="text-sm font-medium text-slate-100 mt-1">{viewPromo.discount_type === 'percent' ? `${viewPromo.discount_value}%` : formatRupiah(viewPromo.discount_value)}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Used / Max</span><p className="text-sm text-slate-200 mt-1">{viewPromo.used_count} / {viewPromo.max_uses}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Status</span><div className="mt-1"><StatusBadge status={viewPromo.is_active ? 'active' : 'inactive'} /></div></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Valid From</span><p className="text-sm text-slate-200 mt-1">{viewPromo.valid_from}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Valid Until</span><p className="text-sm text-slate-200 mt-1">{viewPromo.valid_until}</p></div>
            </div>
            <div className="bg-slate-700/30 rounded-lg p-3">
              <span className="text-xs text-slate-400">Applicable Plans</span>
              <div className="flex gap-1 mt-1">{viewPromo.applicable_plans.map(pl => (<span key={pl} className={`px-2 py-0.5 rounded text-xs font-medium ${pl === 'business' ? 'bg-purple-500/20 text-purple-400' : pl === 'pro' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-600/50 text-slate-400'}`}>{pl}</span>))}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================
// 7. ADMIN AFFILIATES
// ============================================
export function AdminAffiliatesPage() {
  const navigate = useRouter((s) => s.navigate);
  const [activeTab, setActiveTab] = React.useState('semua');

  const tabs = [
    { label: 'Semua', value: 'semua', count: adminAffiliateWithdrawals.length },
    { label: 'Pending', value: 'pending', count: adminAffiliateWithdrawals.filter(w => w.status === 'pending').length },
    { label: 'Approved', value: 'approved', count: adminAffiliateWithdrawals.filter(w => w.status === 'approved').length },
    { label: 'Paid', value: 'paid', count: adminAffiliateWithdrawals.filter(w => w.status === 'paid').length },
  ];

  const filtered = activeTab === 'semua' ? adminAffiliateWithdrawals : adminAffiliateWithdrawals.filter(w => w.status === activeTab);

  return (
    <div className="space-y-6">
      <PageHeader title="Afiliator — Penarikan Dana" subtitle="Kelola permintaan penarikan komisi afiliator" />

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500/20 flex items-center justify-center text-[8px]">⏳</span><span className="text-slate-400"><span className="text-amber-400 font-medium">Pending</span> → Review oleh admin</span></div>
          <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-500/20 flex items-center justify-center text-[8px]">✅</span><span className="text-slate-400"><span className="text-blue-400 font-medium">Approved</span> → Siap dibayarkan</span></div>
          <svg className="w-4 h-4 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500/20 flex items-center justify-center text-[8px]">💰</span><span className="text-slate-400"><span className="text-emerald-400 font-medium">Paid</span> → Selesai</span></div>
        </div>
      </div>

      <TabFilter tabs={tabs} active={activeTab} onChange={setActiveTab} />

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Affiliate</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Email</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase">Amount</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Bank</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Account</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Status</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Date</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                  <td className="px-3 py-2.5 h-10 font-medium text-slate-100">{w.affiliate_name}</td>
                  <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell">{w.affiliate_email}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-400 font-semibold">{formatRupiah(w.amount)}</td>
                  <td className="px-3 py-2.5 text-slate-300 hidden md:table-cell">{w.bank}</td>
                  <td className="px-3 py-2.5 text-slate-400 font-mono hidden lg:table-cell">{w.account}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={w.status} /></td>
                  <td className="px-3 py-2.5 text-slate-400 hidden md:table-cell">{w.date}</td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex gap-1 justify-end">
                      {w.status === 'pending' && (<><button className="px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-medium transition-colors">Approve</button><button className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-[10px] font-medium transition-colors">Reject</button></>)}
                      {w.status === 'approved' && (<button className="px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded text-[10px] font-medium transition-colors">Mark Paid</button>)}
                      {w.status === 'paid' && (<span className="text-slate-500 text-[10px]">Selesai</span>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 8. ADMIN WA GATEWAY (with Create/Edit/View modals)
// ============================================
export function AdminWAGatewayPage() {
  const { toast, show: showToast } = useToast();
  const totalSessions = waNodes.reduce((acc, n) => acc + n.sessions, 0);
  const onlineNodes = waNodes.filter(n => n.status === 'online').length;
  const [nodeStates, setNodeStates] = React.useState<Record<string, string>>(Object.fromEntries(waNodes.map(n => [n.id, n.status])));

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editNode, setEditNode] = React.useState<WANode | null>(null);
  const [viewNode, setViewNode] = React.useState<WANode | null>(null);

  const emptyNodeForm = { name: '', location: 'Singapore' as string, max_sessions: 50, api_key: '' };
  const [form, setForm] = React.useState(emptyNodeForm);

  const openCreate = () => { setForm({ ...emptyNodeForm }); setCreateOpen(true); };
  const openEdit = (n: WANode) => { setForm({ name: n.name, location: n.location, max_sessions: n.max_sessions, api_key: '' }); setEditNode(n); };

  return (
    <div className="space-y-6">
      <PageHeader title="WA Gateway Cluster" subtitle={`${onlineNodes}/5 node online · ${totalSessions} sesi`} actions={<button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">+ Tambah Node</button>} />
      <Toast toast={toast} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {waNodes.map((node) => {
          const sessionPct = node.max_sessions > 0 ? (node.sessions / node.max_sessions) * 100 : 0;
          const cpuColor = node.cpu >= 80 ? 'bg-red-500' : node.cpu >= 60 ? 'bg-amber-500' : 'bg-emerald-500';
          const memColor = node.memory >= 80 ? 'bg-red-500' : node.memory >= 60 ? 'bg-amber-500' : 'bg-emerald-500';
          const status = nodeStates[node.id];

          return (
            <div key={node.id} className={`bg-slate-800 border rounded-xl p-5 transition-all ${status === 'offline' ? 'border-red-500/30' : status === 'degraded' ? 'border-amber-500/30' : 'border-slate-700 hover:border-slate-600'}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${status === 'online' ? 'bg-emerald-500' : status === 'degraded' ? 'bg-amber-500 animate-pulse' : 'bg-red-500'}`} />
                    <h3 className="text-sm font-semibold text-slate-100">{node.name}</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{node.location} · Uptime {node.uptime}</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={status} />
                  <DropdownMenu trigger={<button className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-600/50 transition-colors"><DotsIcon /></button>}>
                    <DropdownItem onClick={() => setViewNode(node)}>View Detail</DropdownItem>
                    <DropdownItem onClick={() => openEdit(node)}>Edit</DropdownItem>
                    <DropdownItem onClick={() => { setNodeStates(s => ({ ...s, [node.id]: s[node.id] === 'online' ? 'offline' : 'online' })); showToast(`Node ${node.name} di-set ${status === 'online' ? 'offline' : 'online'}`); }}>
                      Set {status === 'online' ? 'Offline' : 'Online'}
                    </DropdownItem>
                    <DropdownItem onClick={() => showToast('API Key berhasil di-regenerate')}>Regenerate API Key</DropdownItem>
                    <DropdownItem danger onClick={() => showToast('Node berhasil dihapus')}>Delete</DropdownItem>
                  </DropdownMenu>
                </div>
              </div>

              {status === 'degraded' && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-4">
                  <p className="text-xs text-amber-400 font-medium">⚠️ CPU tinggi ({node.cpu}%) — Pertimbangkan scaling</p>
                </div>
              )}

              <div className="mb-4">
                <div className="flex justify-between mb-1"><span className="text-xs text-slate-400">Sessions</span><span className="text-xs text-slate-300 tabular-nums">{node.sessions}/{node.max_sessions}</span></div>
                <div className="w-full bg-slate-700 rounded-full h-2"><div className={`h-2 rounded-full transition-all ${sessionPct >= 90 ? 'bg-red-500' : 'bg-blue-400'}`} style={{ width: `${sessionPct}%` }} /></div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><div className="flex justify-between mb-1"><span className="text-xs text-slate-400">CPU</span><span className="text-xs text-slate-300 tabular-nums">{node.cpu}%</span></div><div className="w-full bg-slate-700 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${cpuColor}`} style={{ width: `${node.cpu}%` }} /></div></div>
                <div><div className="flex justify-between mb-1"><span className="text-xs text-slate-400">Memory</span><span className="text-xs text-slate-300 tabular-nums">{node.memory}%</span></div><div className="w-full bg-slate-700 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${memColor}`} style={{ width: `${node.memory}%` }} /></div></div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Heartbeat: {node.last_heartbeat}</span>
                <div className="flex gap-1">
                  <button className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700 transition-colors">Restart</button>
                  {status === 'offline' && (<button onClick={() => setNodeStates(s => ({ ...s, [node.id]: 'online' }))} className="text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded hover:bg-emerald-500/10 transition-colors">Start</button>)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Node Modal */}
      <Modal open={createOpen || !!editNode} onClose={() => { setCreateOpen(false); setEditNode(null); }} title={editNode ? 'Edit Node' : 'Tambah Node Baru'} footer={<ModalFooter onCancel={() => { setCreateOpen(false); setEditNode(null); }} onSave={() => { setCreateOpen(false); setEditNode(null); showToast(editNode ? 'Node berhasil diperbarui' : 'Node berhasil dibuat'); }} />}>
        <div className="space-y-4">
          <FormField label="Nama Node" required><FormInput value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="SG Node 03" /></FormField>
          <FormField label="Lokasi" required>
            <FormSelect value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}>
              <option value="Singapore">Singapore</option>
              <option value="Jakarta">Jakarta</option>
              <option value="Surabaya">Surabaya</option>
            </FormSelect>
          </FormField>
          <FormField label="Max Sessions" required><FormInput type="number" value={String(form.max_sessions)} onChange={(e) => setForm(f => ({ ...f, max_sessions: Number(e.target.value) }))} /></FormField>
          <FormField label="API Key" required={!!createOpen}>
            <FormInput type="password" value={form.api_key} onChange={(e) => setForm(f => ({ ...f, api_key: e.target.value }))} placeholder={editNode ? 'Biarkan kosong untuk tidak mengubah' : 'Masukkan API Key'} />
          </FormField>
          {editNode && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Key saat ini:</span>
              <code className="text-xs text-slate-400 font-mono bg-slate-700/50 px-2 py-0.5 rounded">sk-node-●●●●●●●●</code>
              <button onClick={() => showToast('API Key baru telah di-regenerate')} className="text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors">Regenerate</button>
            </div>
          )}
          {editNode && (
            <FormField label="Status">
              <FormSelect value={nodeStates[editNode.id]} onChange={(e) => setNodeStates(s => ({ ...s, [editNode.id]: e.target.value }))}>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </FormSelect>
            </FormField>
          )}
        </div>
      </Modal>

      {/* View Node Detail Modal */}
      <Modal open={!!viewNode} onClose={() => setViewNode(null)} title={`Node: ${viewNode?.name || ''}`} size="lg">
        {viewNode && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">ID</span><p className="text-sm font-mono text-slate-300 mt-1">{viewNode.id}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Lokasi</span><p className="text-sm text-slate-200 mt-1">{viewNode.location}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Status</span><div className="mt-1"><StatusBadge status={nodeStates[viewNode.id]} /></div></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Uptime</span><p className="text-sm text-slate-200 mt-1">{viewNode.uptime}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Heartbeat</span><p className="text-sm text-slate-200 mt-1">{viewNode.last_heartbeat}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">API Key</span><p className="text-sm font-mono text-slate-400 mt-1">sk-node-●●●●●●●●</p></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-slate-400 block mb-2">CPU ({viewNode.cpu}%)</span>
                <div className="w-full bg-slate-700 rounded-full h-3"><div className={`h-3 rounded-full ${viewNode.cpu >= 80 ? 'bg-red-500' : viewNode.cpu >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${viewNode.cpu}%` }} /></div>
              </div>
              <div>
                <span className="text-xs text-slate-400 block mb-2">Memory ({viewNode.memory}%)</span>
                <div className="w-full bg-slate-700 rounded-full h-3"><div className={`h-3 rounded-full ${viewNode.memory >= 80 ? 'bg-red-500' : viewNode.memory >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${viewNode.memory}%` }} /></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-1"><span className="text-xs text-slate-400">Sessions</span><span className="text-xs text-slate-300">{viewNode.sessions}/{viewNode.max_sessions}</span></div>
              <div className="w-full bg-slate-700 rounded-full h-2"><div className="h-2 rounded-full bg-blue-400" style={{ width: `${(viewNode.sessions / viewNode.max_sessions) * 100}%` }} /></div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-300 mb-2">Active Sessions</h4>
              <div className="bg-slate-900/50 rounded-lg p-3">
                {viewNode.sessions > 0 ? (
                  <div className="space-y-1 text-xs text-slate-400">
                    {Array.from({ length: Math.min(viewNode.sessions, 5) }).map((_, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-700/30 rounded px-2 py-1">
                        <span className="font-mono text-slate-300">+62 812-{String(3456 + i * 1111).slice(0, 4)}-XXXX</span>
                        <StatusBadge status="active" />
                      </div>
                    ))}
                    {viewNode.sessions > 5 && <p className="text-slate-500 text-center py-1">...dan {viewNode.sessions - 5} sesi lainnya</p>}
                  </div>
                ) : <p className="text-xs text-slate-500 text-center py-2">Tidak ada sesi aktif</p>}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================
// 9. ADMIN AI PROVIDERS (with Create/Edit Provider & Create Model modals)
// ============================================
export function AdminAIProvidersPage() {
  const { toast, show: showToast } = useToast();
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [providerStates, setProviderStates] = React.useState<Record<string, boolean>>(Object.fromEntries(aiProviders.map(p => [p.id, p.is_active])));

  const [createProviderOpen, setCreateProviderOpen] = React.useState(false);
  const [editProvider, setEditProvider] = React.useState<AIProvider | null>(null);
  const [createModelFor, setCreateModelFor] = React.useState<AIProvider | null>(null);

  const emptyProviderForm = { name: '', slug: '', base_url: '', api_key: '', default_model: '', is_active: true };
  const [providerForm, setProviderForm] = React.useState(emptyProviderForm);
  const emptyModelForm = { name: '', slug: '', context_window: 128000, input_price: 0, output_price: 0, supports_fc: true };
  const [modelForm, setModelForm] = React.useState(emptyModelForm);

  const openCreateProvider = () => { setProviderForm({ ...emptyProviderForm }); setCreateProviderOpen(true); };
  const openEditProvider = (p: AIProvider) => { setProviderForm({ name: p.name, slug: p.slug, base_url: p.base_url, api_key: '', default_model: p.default_model, is_active: p.is_active }); setEditProvider(p); };
  const openCreateModel = (p: AIProvider) => { setModelForm({ ...emptyModelForm }); setCreateModelFor(p); };

  const toggleExpand = (id: string) => { setExpanded(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); };
  const toggleProvider = (id: string) => { setProviderStates(prev => ({ ...prev, [id]: !prev[id] })); };

  const planOptions = aiProviders.flatMap(p => p.models.map(m => ({ provider: p.name, slug: m.slug, name: m.name })));

  return (
    <div className="space-y-6">
      <PageHeader title="AI Providers" actions={<button onClick={openCreateProvider} className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">+ Tambah Provider</button>} />
      <Toast toast={toast} />

      <div className="space-y-4">
        {aiProviders.map((provider) => {
          const isActive = providerStates[provider.id];
          const isExpanded = expanded.has(provider.id);
          return (
            <div key={provider.id} className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <div className="p-5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-sm font-bold text-slate-100">{provider.name}</h3>
                      <StatusBadge status={isActive ? 'active' : 'inactive'} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                      <span>URL: <code className="text-slate-300">{provider.base_url}</code></span>
                      <span>Key: <code className="text-slate-500">{provider.api_key_masked}</code></span>
                      <span>Default: <code className="text-orange-400">{provider.default_model}</code></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu trigger={<button className="bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium px-2 py-1.5 rounded-lg transition-colors flex items-center gap-1"><DotsIcon /></button>}>
                      <DropdownItem onClick={() => showToast(`Provider ${provider.name} view opened`)}>View</DropdownItem>
                      <DropdownItem onClick={() => openEditProvider(provider)}>Edit</DropdownItem>
                      <DropdownItem onClick={() => openCreateModel(provider)}>Add Model</DropdownItem>
                      <DropdownItem onClick={() => showToast('Test koneksi berhasil')}>Test Connection</DropdownItem>
                      <DropdownItem danger onClick={() => showToast('Provider berhasil dihapus')}>Delete</DropdownItem>
                    </DropdownMenu>
                    <button onClick={() => toggleProvider(provider.id)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isActive ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isActive ? 'translate-x-4' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>

                <button onClick={() => toggleExpand(provider.id)} className="mt-3 text-xs text-slate-400 hover:text-white transition-colors flex items-center gap-1">
                  <svg className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  {isExpanded ? 'Sembunyikan' : 'Lihat'} Models ({provider.models.length})
                </button>
              </div>

              {isExpanded && (
                <div className="border-t border-slate-700">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="px-5 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Model</th>
                        <th className="px-5 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Context</th>
                        <th className="px-5 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase">Input/1K</th>
                        <th className="px-5 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase">Output/1K</th>
                        <th className="px-5 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">FC</th>
                        <th className="px-5 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase w-20">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {provider.models.map((model) => (
                        <tr key={model.slug} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors">
                          <td className="px-5 py-2.5 h-10">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-200">{model.name}</span>
                              {model.slug === provider.default_model && <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded text-[10px] font-medium">DEFAULT</span>}
                            </div>
                          </td>
                          <td className="px-5 py-2.5 text-slate-400 tabular-nums">{formatNumber(model.context_window)}</td>
                          <td className="px-5 py-2.5 text-right text-slate-300 tabular-nums">${model.input_price}</td>
                          <td className="px-5 py-2.5 text-right text-slate-300 tabular-nums">${model.output_price}</td>
                          <td className="px-5 py-2.5">{model.supports_fc ? <span className="text-emerald-400 text-[10px]">✅ Yes</span> : <span className="text-slate-500 text-[10px]">❌ No</span>}</td>
                          <td className="px-5 py-2.5 text-right">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => showToast('Model edit opened')} className="px-2 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 rounded text-[10px] font-medium transition-colors">Edit</button>
                              <button onClick={() => showToast('Model berhasil dihapus')} className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-[10px] font-medium transition-colors">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Create/Edit Provider Modal */}
      <Modal open={createProviderOpen || !!editProvider} onClose={() => { setCreateProviderOpen(false); setEditProvider(null); }} title={editProvider ? 'Edit Provider' : 'Tambah Provider Baru'} footer={<ModalFooter onCancel={() => { setCreateProviderOpen(false); setEditProvider(null); }} onSave={() => { setCreateProviderOpen(false); setEditProvider(null); showToast(editProvider ? 'Provider berhasil diperbarui' : 'Provider berhasil dibuat'); }} />}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Nama" required><FormInput value={providerForm.name} onChange={(e) => setProviderForm(f => ({ ...f, name: e.target.value }))} placeholder="OpenAI" /></FormField>
            <FormField label="Slug" required><FormInput value={providerForm.slug} onChange={(e) => setProviderForm(f => ({ ...f, slug: e.target.value }))} placeholder="openai" /></FormField>
          </div>
          <FormField label="Base URL" required><FormInput value={providerForm.base_url} onChange={(e) => setProviderForm(f => ({ ...f, base_url: e.target.value }))} placeholder="https://api.openai.com/v1" /></FormField>
          <FormField label="API Key" required={!!createProviderOpen}>
            <div className="relative">
              <FormInput type="password" value={providerForm.api_key} onChange={(e) => setProviderForm(f => ({ ...f, api_key: e.target.value }))} placeholder={editProvider ? 'Biarkan kosong jika tidak diubah' : 'sk-proj-xxx'} />
              {editProvider && <button onClick={() => showToast('API Key berhasil di-regenerate')} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors">Edit</button>}
            </div>
          </FormField>
          {editProvider && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Key saat ini:</span>
              <code className="text-xs text-slate-400 font-mono bg-slate-700/50 px-2 py-0.5 rounded">{editProvider.api_key_masked}</code>
            </div>
          )}
          <FormField label="Default Model" required>
            <FormSelect value={providerForm.default_model} onChange={(e) => setProviderForm(f => ({ ...f, default_model: e.target.value }))}>
              <option value="">Pilih model...</option>
              {planOptions.map(m => (<option key={m.slug} value={m.slug}>{m.provider} — {m.name}</option>))}
            </FormSelect>
          </FormField>
          <Toggle checked={providerForm.is_active} onChange={(v) => setProviderForm(f => ({ ...f, is_active: v }))} label="Is Active" />
        </div>
      </Modal>

      {/* Create Model Modal */}
      <Modal open={!!createModelFor} onClose={() => setCreateModelFor(null)} title={`Tambah Model — ${createModelFor?.name || ''}`} footer={<ModalFooter onCancel={() => setCreateModelFor(null)} onSave={() => { setCreateModelFor(null); showToast('Model berhasil ditambahkan'); }} />}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Nama Model" required><FormInput value={modelForm.name} onChange={(e) => setModelForm(f => ({ ...f, name: e.target.value }))} placeholder="GPT-5" /></FormField>
            <FormField label="Slug" required><FormInput value={modelForm.slug} onChange={(e) => setModelForm(f => ({ ...f, slug: e.target.value }))} placeholder="gpt-5" /></FormField>
          </div>
          <FormField label="Context Window" required><FormInput type="number" value={String(modelForm.context_window)} onChange={(e) => setModelForm(f => ({ ...f, context_window: Number(e.target.value) }))} /></FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Input Price / 1K tokens ($)" required><FormInput type="number" step="0.01" value={String(modelForm.input_price)} onChange={(e) => setModelForm(f => ({ ...f, input_price: Number(e.target.value) }))} /></FormField>
            <FormField label="Output Price / 1K tokens ($)" required><FormInput type="number" step="0.01" value={String(modelForm.output_price)} onChange={(e) => setModelForm(f => ({ ...f, output_price: Number(e.target.value) }))} /></FormField>
          </div>
          <Toggle checked={modelForm.supports_fc} onChange={(v) => setModelForm(f => ({ ...f, supports_fc: v }))} label="Supports Function Calling" />
        </div>
      </Modal>
    </div>
  );
}

// ============================================
// 10. ADMIN TOOL REGISTRY (with Create/Edit/View modals)
// ============================================
export function AdminToolRegistryPage() {
  const { toast, show: showToast } = useToast();
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [toolStates, setToolStates] = React.useState<Record<string, boolean>>(Object.fromEntries(toolRegistry.map(t => [t.id, t.is_active])));
  const [approvalStates, setApprovalStates] = React.useState<Record<string, boolean>>(Object.fromEntries(toolRegistry.map(t => [t.id, t.requires_approval])));

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editTool, setEditTool] = React.useState<Tool | null>(null);
  const [viewTool, setViewTool] = React.useState<Tool | null>(null);

  const emptyForm = { name: '', display_name: '', category: 'retail', requires_approval: false, is_active: true, schema: '{\n  "type": "object",\n  "properties": {\n    "query": { "type": "string" }\n  },\n  "required": ["query"]\n}' };
  const [form, setForm] = React.useState(emptyForm);

  const openCreate = () => { setForm({ ...emptyForm }); setCreateOpen(true); };
  const openEdit = (t: Tool) => { setForm({ name: t.name, display_name: t.display_name, category: t.category, requires_approval: approvalStates[t.id], is_active: toolStates[t.id], schema: JSON.stringify({ type: 'object', properties: { product_id: { type: 'string' }, query: { type: 'string' } }, required: ['product_id'] }, null, 2) }); setEditTool(t); };

  const toggleExpand = (id: string) => { setExpanded(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); };
  const toggleTool = (id: string) => { setToolStates(prev => ({ ...prev, [id]: !prev[id] })); };
  const toggleApproval = (id: string) => { setApprovalStates(prev => ({ ...prev, [id]: !prev[id] })); };

  return (
    <div className="space-y-6">
      <PageHeader title="Tool Registry" actions={<button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors">+ Tambah Tool</button>} />
      <Toast toast={toast} />

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Name</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase">Display Name</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Category</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-400 uppercase">Approval</th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-400 uppercase">Active</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Used By</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-400 uppercase w-10">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {toolRegistry.map((tool) => {
                const isExpanded = expanded.has(tool.id);
                return (
                  <React.Fragment key={tool.id}>
                    <tr className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                      <td className="px-3 py-2.5 h-10 font-mono text-orange-400">{tool.name}</td>
                      <td className="px-3 py-2.5 font-medium text-slate-100">{tool.display_name}</td>
                      <td className="px-3 py-2.5 hidden md:table-cell">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${tool.category === 'retail' ? 'bg-emerald-500/20 text-emerald-400' : tool.category === 'fnb' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>{tool.category}</span>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button onClick={() => toggleApproval(tool.id)} className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${approvalStates[tool.id] ? 'bg-amber-500' : 'bg-slate-600'}`}><span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${approvalStates[tool.id] ? 'translate-x-3.5' : 'translate-x-0.5'}`} /></button>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button onClick={() => toggleTool(tool.id)} className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${toolStates[tool.id] ? 'bg-emerald-500' : 'bg-slate-600'}`}><span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${toolStates[tool.id] ? 'translate-x-3.5' : 'translate-x-0.5'}`} /></button>
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-300 hidden md:table-cell tabular-nums">{tool.used_by} tenant</td>
                      <td className="px-3 py-2.5 text-right">
                        <DropdownMenu trigger={<button onClick={() => toggleExpand(tool.id)} className="text-slate-400 hover:text-white transition-colors"><DotsIcon /></button>}>
                          <DropdownItem onClick={() => setViewTool(tool)}>View</DropdownItem>
                          <DropdownItem onClick={() => openEdit(tool)}>Edit</DropdownItem>
                          <DropdownItem danger onClick={() => showToast('Tool berhasil dihapus')}>Delete</DropdownItem>
                        </DropdownMenu>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-slate-700/30">
                        <td colSpan={7} className="px-3 py-3 bg-slate-800/50">
                          <pre className="text-[10px] text-slate-400 font-mono bg-slate-900/50 rounded-lg p-3 max-h-32 overflow-auto">
{`{
  "name": "${tool.name}",
  "description": "${tool.display_name}",
  "parameters": {
    "type": "object",
    "properties": {
      "product_id": { "type": "string" },
      "query": { "type": "string" }
    },
    "required": ["product_id"]
  }
}`}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Tool Modal */}
      <Modal open={createOpen || !!editTool} onClose={() => { setCreateOpen(false); setEditTool(null); }} title={editTool ? 'Edit Tool' : 'Tambah Tool Baru'} footer={<ModalFooter onCancel={() => { setCreateOpen(false); setEditTool(null); }} onSave={() => { setCreateOpen(false); setEditTool(null); showToast(editTool ? 'Tool berhasil diperbarui' : 'Tool berhasil dibuat'); }} />}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Name" required><FormInput value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="cek_stok" /></FormField>
            <FormField label="Display Name" required><FormInput value={form.display_name} onChange={(e) => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="Cek Stok Produk" /></FormField>
          </div>
          <FormField label="Category" required>
            <FormSelect value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}>
              <option value="retail">Retail</option>
              <option value="fnb">FnB</option>
              <option value="rental">Rental</option>
              <option value="general">General</option>
            </FormSelect>
          </FormField>
          <div className="flex gap-4">
            <Toggle checked={form.requires_approval} onChange={(v) => setForm(f => ({ ...f, requires_approval: v }))} label="Requires Approval" />
            <Toggle checked={form.is_active} onChange={(v) => setForm(f => ({ ...f, is_active: v }))} label="Is Active" />
          </div>
          <FormField label="JSON Schema (parameters_schema)">
            <FormTextarea value={form.schema} onChange={(e) => setForm(f => ({ ...f, schema: e.target.value }))} rows={8} className="font-mono text-xs" />
          </FormField>
        </div>
      </Modal>

      {/* View Tool Modal */}
      <Modal open={!!viewTool} onClose={() => setViewTool(null)} title={viewTool?.display_name || ''} size="lg">
        {viewTool && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Name</span><p className="text-sm font-mono text-orange-400 mt-1">{viewTool.name}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Display Name</span><p className="text-sm font-medium text-slate-100 mt-1">{viewTool.display_name}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Category</span><p className="text-sm text-slate-200 capitalize mt-1">{viewTool.category}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Used By</span><p className="text-sm text-slate-200 mt-1">{viewTool.used_by} tenant</p></div>
            </div>
            <div className="flex gap-4">
              <div className="bg-slate-700/30 rounded-lg px-3 py-2"><span className="text-xs text-slate-400">Approval: </span><StatusBadge status={approvalStates[viewTool.id] ? 'pending' : 'active'} variant={approvalStates[viewTool.id] ? 'pending' : 'active'} /></div>
              <div className="bg-slate-700/30 rounded-lg px-3 py-2"><span className="text-xs text-slate-400">Active: </span><StatusBadge status={toolStates[viewTool.id] ? 'active' : 'inactive'} /></div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-300 mb-2">Parameters Schema</h4>
              <div className="bg-slate-900 rounded-lg border border-slate-700 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-700/30 border-b border-slate-700">
                  <span className="text-[10px] text-slate-500 font-mono">JSON Schema</span>
                </div>
                <pre className="p-3 text-xs text-slate-300 font-mono overflow-auto max-h-48">
{JSON.stringify({
  name: viewTool.name,
  description: viewTool.display_name,
  parameters: {
    type: 'object',
    properties: {
      product_id: { type: 'string', description: 'ID produk' },
      query: { type: 'string', description: 'Pencarian' }
    },
    required: ['product_id']
  }
}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================
// 11. ADMIN AUDIT LOGS (with View Detail modal)
// ============================================
export function AdminAuditLogsPage() {
  const [search, setSearch] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('semua');
  const [severityFilter, setSeverityFilter] = React.useState('semua');
  const [viewLog, setViewLog] = React.useState<AuditLog | null>(null);

  const severityDot: Record<string, string> = { critical: '🔴', warning: '🟡', info: '🔵', success: '🟢' };

  const filtered = auditLogs.filter(l => {
    if (search && !l.event.includes(search) && !l.user.includes(search) && !l.target.includes(search)) return false;
    if (categoryFilter !== 'semua' && l.category !== categoryFilter) return false;
    if (severityFilter !== 'semua' && l.severity !== severityFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Audit Logs" subtitle={`${auditLogs.length} events tercatat`} />

      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 max-w-sm"><SearchInput placeholder="Cari event, user, target..." value={search} onChange={setSearch} /></div>
        <div className="flex gap-2 flex-wrap">
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
            <option value="semua">Semua Category</option>
            <option value="auth">Auth</option><option value="tenant">Tenant</option><option value="subscription">Subscription</option><option value="user">User</option><option value="agent">Agent</option><option value="security">Security</option><option value="system">System</option>
          </select>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
            <option value="semua">Semua Severity</option>
            <option value="critical">Critical</option><option value="warning">Warning</option><option value="info">Info</option><option value="success">Success</option>
          </select>
          <input type="date" className="bg-slate-800 border border-slate-700 text-xs text-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
        </div>
      </div>

      <div className="space-y-2">
        {filtered.map((log) => (
          <div key={log.id} onClick={() => setViewLog(log)} className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 hover:border-slate-600 transition-colors cursor-pointer">
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm shrink-0">{severityDot[log.severity] || '⚪'}</span>
                <StatusBadge status={log.category} variant={log.category} />
                <span className="font-mono text-xs text-slate-200 font-medium truncate">{log.event}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span className="truncate max-w-[150px]">{log.user}</span>
                <span className="text-slate-600">→</span>
                <span className="truncate max-w-[200px]">{log.target}</span>
              </div>
              <div className="flex items-center gap-3">
                <code className="text-[10px] text-slate-500 font-mono hidden lg:block max-w-[200px] truncate">{log.changes}</code>
                <span className="text-[10px] text-slate-500 tabular-nums whitespace-nowrap">{log.timestamp}</span>
                <button className="text-orange-400 hover:text-orange-300 text-[10px] font-medium whitespace-nowrap transition-colors">Detail →</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View Log Detail Modal */}
      <Modal open={!!viewLog} onClose={() => setViewLog(null)} title="Detail Audit Log">
        {viewLog && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-lg">{severityDot[viewLog.severity] || '⚪'}</span>
              <StatusBadge status={viewLog.category} variant={viewLog.category} />
              <StatusBadge status={viewLog.severity} variant={viewLog.severity} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Event</span><p className="text-sm font-mono text-slate-200 mt-1">{viewLog.event}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Category</span><p className="text-sm text-slate-200 capitalize mt-1">{viewLog.category}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">User</span><p className="text-sm text-slate-200 mt-1">{viewLog.user}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Target</span><p className="text-sm text-slate-200 mt-1">{viewLog.target}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">Timestamp</span><p className="text-sm text-slate-200 tabular-nums mt-1">{viewLog.timestamp}</p></div>
              <div className="bg-slate-700/30 rounded-lg p-3"><span className="text-xs text-slate-400">IP Address</span><p className="text-sm text-slate-200 font-mono mt-1">{viewLog.target.includes('IP:') ? viewLog.target.split('IP: ')[1] : 'N/A'}</p></div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-slate-300 mb-2">Changes</h4>
              <div className="bg-slate-900 rounded-lg border border-slate-700 p-3">
                <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap">{viewLog.changes}</pre>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================
// 12. ADMIN SECURITY
// ============================================
export function AdminSecurityPage() {
  const navigate = useRouter((s) => s.navigate);
  const sd = securityDashboard;
  const maxCount = Math.max(...sd.failed_login_chart.map(h => h.count));

  return (
    <div className="space-y-6">
      <PageHeader title="Security" />

      <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm text-red-400 font-medium">{sd.suspect_ips} IP mencurigakan terdeteksi</span>
        </div>
        <button className="text-xs text-red-300 hover:text-white font-medium transition-colors">Lihat Detail →</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatsCard title="Login Gagal (24h)" value={String(sd.failed_logins_24h)} iconBg="bg-red-500/20" iconColor="text-red-400" />
        <StatsCard title="Rate Limit Hari Ini" value={`${sd.rate_limits_today}x`} iconBg="bg-amber-500/20" iconColor="text-amber-400" />
        <StatsCard title="Suspect IP" value={String(sd.suspect_ips)} iconBg="bg-red-500/20" iconColor="text-red-400" />
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-200 mb-4">Login Gagal per Jam (24 jam terakhir)</h3>
        <div className="flex items-end gap-1 h-32">
          {sd.failed_login_chart.map((h) => (
            <div key={h.hour} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-slate-700/50 rounded-t flex items-end" style={{ height: '100px' }}>
                <div className="w-full bg-red-500/70 rounded-t transition-all duration-500" style={{ height: `${maxCount > 0 ? (h.count / maxCount) * 100 : 0}%` }} />
              </div>
              <span className="text-[9px] text-slate-500 tabular-nums">{h.hour}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">IP Mencurigakan</h3>
          <div className="space-y-3">
            {sd.suspect_ip_list.map((ip) => (
              <div key={ip.ip} className="flex items-center justify-between bg-slate-700/30 rounded-lg px-3 py-2.5">
                <div>
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" /><code className="text-xs text-slate-100 font-mono font-semibold">{ip.ip}</code><span className="text-[10px] text-slate-500">{ip.location}</span></div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500"><span>Login gagal: <span className="text-red-400 font-medium">{ip.failed_count}x</span></span><span>Terakhir: {ip.last_seen}</span></div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded text-[10px] font-medium transition-colors">Blokir IP</button>
                  <button className="px-2 py-1 bg-slate-600/30 hover:bg-slate-600/50 text-slate-400 rounded text-[10px] font-medium transition-colors">Lihat Log</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Event Anomali</h3>
          <div className="space-y-3">
            {sd.anomaly_events.map((a, i) => (
              <div key={i} className="bg-slate-700/30 rounded-lg px-3 py-2.5">
                <div className="flex items-center gap-2 mb-1"><StatusBadge status={a.severity} /><span className="text-[10px] text-slate-500 tabular-nums">{a.time}</span></div>
                <p className="text-xs text-slate-300">{a.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 13. ADMIN SYSTEM SETTINGS
// ============================================
export function AdminSystemSettingsPage() {
  const [activeTab, setActiveTab] = React.useState('umum');
  const settings = systemSettings;

  const [form, setForm] = React.useState(settings.general);
  const [midtransEnv, setMidtransEnv] = React.useState(settings.payment.midtrans_env);
  const [featureFlags, setFeatureFlags] = React.useState(settings.features);
  const [connectionTested, setConnectionTested] = React.useState(false);

  const tabList = [
    { label: 'Umum', value: 'umum' },
    { label: 'Branding', value: 'branding' },
    { label: 'Payment', value: 'payment' },
    { label: 'Email', value: 'email' },
    { label: 'Fitur', value: 'fitur' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="System Settings" />

      <div className="flex gap-1 bg-slate-800 rounded-lg p-1 w-fit">
        {tabList.map((tab) => (
          <button key={tab.value} onClick={() => setActiveTab(tab.value)} className={`px-4 py-2 rounded-md text-xs font-medium transition-all ${activeTab === tab.value ? 'bg-orange-500/20 text-orange-400' : 'text-slate-400 hover:text-white'}`}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'umum' && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Pengaturan Umum</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-400 mb-1.5">App Name</label><input type="text" value={form.app_name} onChange={(e) => setForm(prev => ({ ...prev, app_name: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">Tagline</label><input type="text" value={form.tagline} onChange={(e) => setForm(prev => ({ ...prev, tagline: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">App URL</label><input type="url" value={form.app_url} onChange={(e) => setForm(prev => ({ ...prev, app_url: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">Support Email</label><input type="email" value={form.support_email} onChange={(e) => setForm(prev => ({ ...prev, support_email: e.target.value }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50" /></div>
          </div>
          <div className="mt-5 flex justify-end"><button className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-5 py-2 rounded-lg transition-colors">Simpan</button></div>
        </div>
      )}

      {activeTab === 'branding' && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Branding</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-400 mb-1.5">Primary Color</label><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-orange-600 border border-slate-600" /><input type="text" value="#EA580C" readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">Logo URL</label><input type="text" value="https://rekanku.id/logo.png" readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">Favicon URL</label><input type="text" value="https://rekanku.id/favicon.ico" readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
          </div>
          <div className="mt-5 flex justify-end"><button className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-5 py-2 rounded-lg transition-colors">Simpan</button></div>
        </div>
      )}

      {activeTab === 'payment' && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Midtrans Configuration</h3>
          <div className="flex items-center justify-between mb-5 p-3 bg-slate-700/30 rounded-lg">
            <div><span className="text-xs text-slate-300 font-medium">Environment</span><p className="text-[10px] text-slate-500 mt-0.5">Production mode mengaktifkan pembayaran nyata</p></div>
            <button onClick={() => setMidtransEnv(prev => prev === 'production' ? 'sandbox' : 'production')} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${midtransEnv === 'production' ? 'bg-emerald-500' : 'bg-slate-600'}`}><span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${midtransEnv === 'production' ? 'translate-x-4' : 'translate-x-1'}`} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-400 mb-1.5">Client Key</label><input type="text" value={settings.payment.midtrans_client_key} readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">Server Key</label><input type="password" value={settings.payment.midtrans_server_key} readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <button onClick={() => setConnectionTested(true)} className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-medium px-4 py-2 rounded-lg transition-colors">Test Connection</button>
            {connectionTested && (<div className="flex items-center gap-1.5 text-xs text-emerald-400"><svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg><span>Connected — {midtransEnv}</span></div>)}
          </div>
        </div>
      )}

      {activeTab === 'email' && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Email Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-400 mb-1.5">SMTP Host</label><input type="text" value="smtp.rekanku.id" readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">SMTP Port</label><input type="text" value="587" readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">From Email</label><input type="email" value="noreply@rekanku.id" readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
            <div><label className="block text-xs text-slate-400 mb-1.5">From Name</label><input type="text" value="Rekanku" readOnly className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-400 font-mono" /></div>
          </div>
          <div className="mt-5 flex justify-end"><button className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium px-5 py-2 rounded-lg transition-colors">Simpan</button></div>
        </div>
      )}

      {activeTab === 'fitur' && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Feature Flags & Configuration</h3>
          <div className="space-y-4">
            <div className="p-3 bg-slate-700/30 rounded-lg">
              <h4 className="text-xs font-semibold text-slate-200 mb-3">Program Affiliate</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Aktif</span><button onClick={() => setFeatureFlags(prev => ({ ...prev, affiliate_enabled: !prev.affiliate_enabled }))} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${featureFlags.affiliate_enabled ? 'bg-emerald-500' : 'bg-slate-600'}`}><span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${featureFlags.affiliate_enabled ? 'translate-x-4' : 'translate-x-1'}`} /></button></div>
                <div><label className="block text-xs text-slate-400 mb-1">Commission Rate</label><input type="number" value={featureFlags.commission_rate} onChange={(e) => setFeatureFlags(prev => ({ ...prev, commission_rate: Number(e.target.value) }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500/50" /></div>
                <div><label className="block text-xs text-slate-400 mb-1">Min Withdrawal</label><input type="number" value={featureFlags.min_withdrawal} onChange={(e) => setFeatureFlags(prev => ({ ...prev, min_withdrawal: Number(e.target.value) }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500/50" /></div>
              </div>
            </div>
            <div className="p-3 bg-slate-700/30 rounded-lg">
              <h4 className="text-xs font-semibold text-slate-200 mb-3">Trial & Maintenance</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-xs text-slate-400 mb-1">Trial Days</label><input type="number" value={featureFlags.trial_days} onChange={(e) => setFeatureFlags(prev => ({ ...prev, trial_days: Number(e.target.value) }))} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500/50" /></div>
                <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Maintenance Mode</span><button onClick={() => setFeatureFlags(prev => ({ ...prev, maintenance_mode: !prev.maintenance_mode }))} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${featureFlags.maintenance_mode ? 'bg-red-500' : 'bg-slate-600'}`}><span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${featureFlags.maintenance_mode ? 'translate-x-4' : 'translate-x-1'}`} /></button></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
